import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const SCROLL_AMOUNT = 300;
const IMAGE_CYCLE_INTERVAL = 3000;

const ProjectSection = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleScrollButtons, setVisibleScrollButtons] = useState({ left: false, right: false });
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const scrollRef = useRef(null);

  // ✅ Fetch & reorder projects: [3,0,1,2]
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/projects`)
      .then(({ data }) => {
        const transformed = data.map(p => ({
          ...p,
          imageUrl: Array.isArray(p.imageUrl)
            ? p.imageUrl
            : (p.imageUrl ? [{ url: p.imageUrl, description: '' }] : []),
        }));

        const order = [3, 0, 1, 2];

        const reordered =
          transformed.length >= 4
            ? order.map(i => transformed[i]).filter(Boolean)
            : transformed;

        setProjects(reordered);
      })
      .catch(err => {
        console.error('Failed to load projects:', err);
        setProjects([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const canScroll = scrollWidth > clientWidth;
    setVisibleScrollButtons({
      left: canScroll && scrollLeft > 0,
      right: canScroll && scrollLeft + clientWidth < scrollWidth - 1,
    });
  }, []);

  const handleScroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    updateScrollButtons();
    const onScroll = () => updateScrollButtons();
    const onResize = () => updateScrollButtons();

    container.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      resizeObserver.disconnect();
    };
  }, [updateScrollButtons]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (selectedProject) {
          setSelectedProject(null);
          setCurrentContentIndex(0);
        }
        if (fullScreenImage) {
          setFullScreenImage(null);
        }
      }
    };
    if (selectedProject || fullScreenImage) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedProject, fullScreenImage]);

  const totalModalSlides = selectedProject ? 1 + selectedProject.imageUrl.length : 0;

  const handlePrevContent = () => {
    setCurrentContentIndex(prev => prev === 0 ? totalModalSlides - 1 : prev - 1);
  };

  const handleNextContent = () => {
    setCurrentContentIndex(prev => prev === totalModalSlides - 1 ? 0 : prev + 1);
  };

  const renderProjectCards = () => {
    if (loading || (projects.length === 0 && !loading)) {
      const skeletonCount = 4;
      return Array.from({ length: skeletonCount }).map((_, index) => (
        <ProjectCardSkeleton key={index} />
      ));
    } else {
      return projects.map(project => (
        <ProjectCardPreview
          key={project._id}
          project={project}
          setSelectedProject={setSelectedProject}
          setCurrentContentIndex={setCurrentContentIndex}
        />
      ));
    }
  };

  return (
    <section id="portfolio-projects" className="projectSection-scrollWrapper">
      <h2 className="projectsSectiontitle">Projects</h2>

      <div className="scrollContainer">
        {visibleScrollButtons.left && !loading && projects.length > 0 && (
          <button className="scroll-btn left" onClick={() => handleScroll('left')}>
            <ArrowBackIosNewIcon />
          </button>
        )}

        <div className="projectsRow" ref={scrollRef}>
          {renderProjectCards()}
        </div>

        {visibleScrollButtons.right && !loading && projects.length > 0 && (
          <button className="scroll-btn right" onClick={() => handleScroll('right')}>
            <ArrowForwardIosIcon />
          </button>
        )}
      </div>

      {selectedProject && (
        <div className="modalOverlay" onClick={() => { setSelectedProject(null); setCurrentContentIndex(0); }}>
          <div className="modalContent project-view-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modalCloseButton" onClick={() => { setSelectedProject(null); setCurrentContentIndex(0); }}>&times;</button>

            {totalModalSlides > 1 && (
              <div className="content-carousel-nav-wrapper">
                <button className="carousel-nav-btn left" onClick={handlePrevContent}><ArrowBackIosNewIcon /></button>
                <button className="carousel-nav-btn right" onClick={handleNextContent}><ArrowForwardIosIcon /></button>
                <div className="carousel-content-counter">{currentContentIndex + 1} / {totalModalSlides}</div>
              </div>
            )}

            <div className="modal-content-slide-wrapper">
              {currentContentIndex === 0 && (
                <div className="modal-slide main-description-slide">
                  <h3 className="project-modal-title">{selectedProject.title}</h3>
                  <p className="project-modal-description">{selectedProject.description}</p>
                  {selectedProject.techStack?.length > 0 && (
                    <div className="project-modal-tech-stack">
                      <h4>Tech Stack:</h4>
                      <ul>
                        {selectedProject.techStack.map((tech, i) => <li key={i}>{tech}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="project-modal-links">
                    {selectedProject.githubUrl && <a href={selectedProject.githubUrl} target="_blank" rel="noopener noreferrer">GitHub</a>}
                    {selectedProject.liveUrl && <a href={selectedProject.liveUrl} target="_blank" rel="noopener noreferrer">Live</a>}
                  </div>
                </div>
              )}

              {selectedProject.imageUrl.map((img, i) => (
                currentContentIndex === (i + 1) && (
                  <div className="modal-slide image-slide" key={i}>
                    {img.url ? (
                      <div className="project-modal-image-carousel-content">
                        <img
                          src={img.url}
                          alt={`${selectedProject.title} screenshot ${i + 1}`}
                          className="project-modal-image"
                          onClick={() => setFullScreenImage(img)}
                        />
                        {img.description && <div className="carousel-image-description">{img.description}</div>}
                      </div>
                    ) : <div className="no-image-placeholder">No image available for this slide.</div>}
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
      )}

      {fullScreenImage && (
        <div className="modalOverlay full-screen-image-overlay" onClick={() => setFullScreenImage(null)}>
          <div className="full-screen-image-container" onClick={(e) => e.stopPropagation()}>
            <div className="modalCloseButton" onClick={() => setFullScreenImage(null)}>&times;</div>
            <img src={fullScreenImage.url} alt="Full Screen" className="full-screen-image" />
          </div>
        </div>
      )}
    </section>
  );
};

const ProjectCardSkeleton = () => (
  <div className="projectCardWrapper skeleton">
    <div className="project-card-inline">
      <div className="skeleton-image"></div>
      <div className="skeleton-content">
        <div className="skeleton-title-line"></div>
        <div className="skeleton-description-line"></div>
        <div className="skeleton-description-line short"></div>
        <div className="skeleton-tech-stack">
          <div className="skeleton-pill"></div>
          <div className="skeleton-pill"></div>
          <div className="skeleton-pill"></div>
        </div>
        <div className="skeleton-links">
          <div className="skeleton-button"></div>
          <div className="skeleton-button"></div>
        </div>
      </div>
    </div>
  </div>
);

const ProjectCardPreview = ({ project, setSelectedProject, setCurrentContentIndex }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const descriptionRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (!project.imageUrl || project.imageUrl.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % project.imageUrl.length);
    }, IMAGE_CYCLE_INTERVAL);
    return () => clearInterval(interval);
  }, [project.imageUrl]);

  useEffect(() => {
    const checkTruncation = () => {
      const element = descriptionRef.current;
      if (element) {
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);

    let observer;
    if (descriptionRef.current) {
      observer = new MutationObserver(checkTruncation);
      observer.observe(descriptionRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    }
    return () => {
      window.removeEventListener('resize', checkTruncation);
      if (observer) observer.disconnect();
    };
  }, [project.description]);

  return (
    <div className="projectCardWrapper" onClick={() => { setSelectedProject(project); setCurrentContentIndex(0); }}>
      <div className="project-card-inline">
        <img
          src={project.imageUrl?.[currentImageIndex]?.url || ''}
          alt={project.title}
          className="project-card-inline-image"
        />
        <h3 className="project-card-inline-title">
          {project.title}
        </h3>
        <p className="project-card-inline-description" ref={descriptionRef}>
          {project.description}
          {isTruncated && <span className="see-more-link">&nbsp;See more</span>}
        </p>
        <div className="project-card-inline-tech-stack">
          {project.techStack.map((tech, i) => (
            <span key={i} className="tech-pill">{tech}</span>
          ))}
        </div>
        <div className="project-card-inline-links">
          {project.githubUrl && (
            <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="project-card-inline-link" onClick={(e) => e.stopPropagation()}>
              GitHub
            </a>
          )}
          {project.liveUrl && (
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="project-card-inline-link" onClick={(e) => e.stopPropagation()}>
              Visit
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectSection;
